import { FocusMonitor, HighContrastMode, HighContrastModeDetector, LiveAnnouncer } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIcon } from '../icon/icon';
import { HxTooltipDirective } from '../tooltip/tooltip';

/** Semantic context determining color scheme. */
export type NoteContext = 'info' | 'success' | 'warning' | 'danger' | 'neutral' | 'none';

/** Text alignment for note content. */
export type NoteJustification = 'left' | 'center' | 'right';

/** Visual appearance style for note. */
export type NoteAppearance = 'subtle' | 'outlined' | 'solid';

type NoteFontStyle = { weight?: string; style?: string; decoration?: string };

/**
 * HxNote Component
 *
 * Semantic note with width control and content justification. Supports HTML-based rich text formatting with
 * DomSanitizer security.
 *
 * @example
 *   ```html
 *   <hx-note
 *     context="info"
 *     description="Your changes have been saved."
 *     width="50%"
 *     contentJustification="center"
 *   />
 *   ```;
 *
 * @accessibility
 * - WCAG 2.1 AA compliant structure
 * - DomSanitizer prevents XSS attacks
 * - Semantic HTML with proper roles
 */
@Component({
  selector: 'hx-note',
  standalone: true,
  imports: [HxIcon, HxTooltipDirective],
  templateUrl: './note.html',
  styleUrl: './note.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '[class.hx-note--dismissed]': 'dismissed()',
    '[class.hx-note--high-contrast]': 'isHighContrast()',
    '[style]': 'hostStyles()',
    '[style.--hx-note-max-height]': 'maxHeight()',
  },
})
export class HxNote extends HxBaseControl implements AfterViewInit {
  // Dependencies (inject calls)
  /** DomSanitizer service for HTML content sanitization. */
  private readonly sanitizer = inject(DomSanitizer);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly highContrastDetector = inject(HighContrastModeDetector);

  // Inputs
  /**
   * Semantic context determining color scheme and visual treatment.
   *
   * Available contexts:
   *
   * - `'none'`: Transparent background, no default styling (minimal visual treatment)
   * - `'info'`: Blue color scheme for informational messages
   * - `'success'`: Green color scheme for success confirmations and positive feedback
   * - `'warning'`: Orange/yellow color scheme for warnings and caution messages
   * - `'danger'`: Red color scheme for errors, critical alerts, and destructive actions
   * - `'neutral'`: Gray color scheme for neutral, general-purpose messages
   *
   * Each context automatically applies semantic colors to background, text, and borders.
   *
   * @default 'none'
   */
  readonly context = input<NoteContext>('none');

  /**
   * Visual appearance style.
   *
   * @default 'subtle'
   */
  readonly appearance = input<NoteAppearance>('subtle');

  /**
   * Whether note can be dismissed with close button.
   *
   * @default false
   */
  readonly dismissible = input<boolean>(false);

  /** Optional note title (bold text above description). */
  readonly title = input<string>();

  /** Optional subtitle displayed below the title. */
  readonly subtitle = input<string>();

  /**
   * Width of the note container using CSS length values.
   *
   * Accepts any valid CSS width value including:
   *
   * - Percentage: `'50%'`, `'100%'`
   * - Pixels: `'300px'`, `'500px'`
   * - Relative units: `'20rem'`, `'10em'`
   * - Viewport units: `'50vw'`, `'80vw'`
   *
   * Invalid values log a console warning and fallback to '100%'. Useful for multi-column layouts and responsive
   * designs.
   *
   * @default '100%'
   */
  readonly width = input<string>('100%');

  /**
   * Horizontal text alignment for note content.
   *
   * Available alignments:
   *
   * - `'left'`: Align content to the left edge (default, most readable for LTR languages)
   * - `'center'`: Center-align content (useful for emphasized messages or headlines)
   * - `'right'`: Align content to the right edge (useful for RTL languages or special layouts)
   *
   * Applies to all text and HTML content within the note.
   *
   * @default 'left'
   */
  readonly contentJustification = input<NoteJustification>('left');

  /**
   * Maximum height of the note container. When set, enables vertical scrollbar if content exceeds this height.
   *
   * Accepts any valid CSS height value including:
   *
   * - Pixels: `'200px'`, `'400px'`
   * - Relative units: `'10rem'`, `'20em'`
   * - Viewport units: `'50vh'`
   *
   * When empty or not set, note height is unconstrained and grows with content. When set, content exceeding this height
   * will be scrollable with a styled scrollbar:
   *
   * - Vertical scrollbar appears automatically when content overflows
   * - Scrollbar is styled consistently with the design system (thin, semi-transparent)
   * - Horizontal overflow is hidden to prevent layout breaks
   * - Works with both Firefox (scrollbar-width) and Webkit browsers (pseudo-elements)
   *
   * @default undefined (unconstrained height)
   */
  readonly maxHeight = input<string | undefined>(undefined);

  /**
   * Note message content as an HTML string.
   *
   * Supports rich text formatting including:
   *
   * - Bold: `<b>text</b>` or `<strong>text</strong>`
   * - Italic: `<i>text</i>` or `<em>text</em>`
   * - Underline: `<u>text</u>`
   * - Strikethrough: `<s>text</s>` or `<del>text</del>`
   * - Links: `<a href="url">text</a>`
   *
   * Content is automatically sanitized via Angular's DomSanitizer to prevent XSS attacks while preserving intentional
   * HTML formatting. Potentially dangerous content is stripped.
   *
   * @required
   */
  readonly description = input.required<string>();

  /**
   * Custom background color for the note container.
   *
   * Accepts any valid CSS color value including:
   *
   * - Hex: `'#E3F2FD'`, `'#FFF3E0'`
   * - RGB/RGBA: `'rgb(227, 242, 253)'`, `'rgba(227, 242, 253, 0.5)'`
   * - HSL/HSLA: `'hsl(207, 89%, 86%)'`, `'hsla(207, 89%, 86%, 0.5)'`
   * - Named colors: `'lightblue'`, `'cornsilk'`
   *
   * When set, overrides the default context-based background colors (info, success, warning, danger, neutral). Text
   * color is kept black for readability.
   *
   * @default undefined
   */
  readonly backgroundColor = input<string | undefined>(undefined);

  // Outputs
  /** Emitted when note is dismissed via close button. Parent responsible for removing note from DOM. */
  readonly onDismiss = output<void>();

  // State signals
  /** Internal dismissed state for exit animation. */
  protected readonly dismissed = signal<boolean>(false);

  // ViewChild queries
  /** Close button element reference for focus monitoring. */
  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeButton');

  // Computed values
  /** Computed sanitized HTML content. Prevents XSS attacks while allowing intentional HTML formatting. */
  readonly sanitizedHtml = computed(() => {
    const content = this.description();
    return this.sanitizer.bypassSecurityTrustHtml(content);
  });

  /** Resolved heading text used for rendering and accessibility. */
  readonly headingText = computed(() => this.label() ?? this.title() ?? '');

  /** Maps font weight selection to CSS properties. */
  private mapFontWeight(value: string | undefined): NoteFontStyle {
    switch (value) {
      case 'Light':
        return { weight: '300' };
      case 'Regular':
        return { weight: '400' };
      case 'Medium':
        return { weight: '500' };
      case 'Semi Bold':
        return { weight: '600' };
      case 'Bold':
        return { weight: '700' };
      case 'Italic':
        return { style: 'italic' };
      case 'Underline':
        return { decoration: 'underline' };
      default:
        return {};
    }
  }

  /** Computed CSS font-weight for label text. */
  readonly labelFontWeightStyle = computed(() => this.mapFontWeight(this.labelFontWeight()).weight);

  /** Computed CSS font-style for label text. */
  readonly labelFontStyle = computed(() => this.mapFontWeight(this.labelFontWeight()).style);

  /** Computed CSS text-decoration for label text. */
  readonly labelTextDecoration = computed(() => this.mapFontWeight(this.labelFontWeight()).decoration);

  /** Computed CSS font-size for label text. */
  readonly labelFontSizeStyle = computed(() => {
    const size = this.labelFontSize();
    return size ? `${size}px` : undefined;
  });

  /** Computed CSS font-weight for subtitle text. */
  readonly subtitleFontWeightStyle = computed(() => this.mapFontWeight(this.subtitleFontWeight()).weight);

  /** Computed CSS font-style for subtitle text. */
  readonly subtitleFontStyle = computed(() => this.mapFontWeight(this.subtitleFontWeight()).style);

  /** Computed CSS text-decoration for subtitle text. */
  readonly subtitleTextDecoration = computed(() => this.mapFontWeight(this.subtitleFontWeight()).decoration);

  /** Computed CSS font-size for subtitle text. */
  readonly subtitleFontSizeStyle = computed(() => {
    const size = this.subtitleFontSize();
    return size ? `${size}px` : undefined;
  });

  /**
   * Computed validated width with fallback to 100%. Validates percentage format; invalid values trigger console warning
   * and fallback.
   */
  readonly validatedWidth = computed(() => {
    const w = this.width();
    // Empty strings use default
    if (!w || w.trim() === '') {
      return '100%';
    }
    if (this.isValidWidth(w)) {
      return w;
    }
    console.warn(`Invalid width '${w}' provided to hx-note; using default 100%`);
    return '100%';
  });

  /** Computed host CSS classes combining component base class and scrollable modifier when maxHeight is set. */
  readonly hostClasses = computed(() => {
    const classes = ['hx-note', `hx-note--${this.context()}`, `hx-note--${this.appearance()}`];

    // Add scrollable modifier class when maxHeight is set
    if (this.maxHeight()) {
      classes.push('hx-note--scrollable');
    }

    return classes.filter(Boolean).join(' ');
  });

  /** Detect Windows High Contrast Mode. */
  protected readonly isHighContrast = computed(() => {
    return this.highContrastDetector.getHighContrastMode() !== HighContrastMode.NONE;
  });

  /** Computed host styles combining width control. */
  readonly hostStyles = computed(() => {
    const styles: string[] = ['display: block'];

    const width = this.validatedWidth();
    if (width) {
      styles.push(`width: ${width}`);
    }

    return styles.join('; ');
  });

  // Lifecycle hooks
  ngAfterViewInit(): void {
    const closeButton = this.closeButtonRef();
    if (closeButton) {
      this.focusMonitor.monitor(closeButton.nativeElement, true);
    }
  }

  // Methods
  /** Validate width format. Accepts: percentage (e.g., "50%"), pixels (e.g., "400px"), rems (e.g., "20rem"), etc. */
  private isValidWidth(width: string): boolean {
    if (!width || width.trim() === '') {
      // Empty or whitespace-only strings are valid (will use default)
      return true;
    }
    // Accept any CSS length value (%, px, rem, em, vw, etc.)
    const validPattern = /^(\d+(\.\d+)?)(px|rem|em|%|vw|vh|vmin|vmax|ch|ex|cm|mm|in|pt|pc)$/;
    return validPattern.test(width.trim());
  }

  /** Handle note dismissal. Sets dismissed state, announces to screen readers, and emits event. */
  protected handleDismiss(): void {
    this.dismissed.set(true);
    void this.liveAnnouncer.announce('Note dismissed', 'polite');

    setTimeout(() => {
      this.onDismiss.emit();
    }, 150);
  }

  /** Handle Escape key for dismissal. */
  protected handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.dismissible()) {
      event.preventDefault();
      this.handleDismiss();
    }
  }
}
